// ============================================================================
// P-SEARCH.JS - Search Template Specific Logic
// ============================================================================
// Purpose: Handle search-template specific behavior only
// - Search filters/facets initialization
// - Search results pagination/infinite scroll hooks
// - NOT predictive search (that's header UI, belongs in theme.js)

// ============================================================================
// CONFIGURATION & CONSTANTS
// ============================================================================
const SEARCH_SELECTORS = {
  resultsContainer: '[data-search-results]',
  filterForm: '[data-search-filters]',
  sortSelect: '[data-search-sort]',
  paginationContainer: '[data-search-pagination]',
  infiniteScrollTrigger: '[data-infinite-scroll-trigger]',
};

// ============================================================================
// SEARCH MODULE
// ============================================================================
const SearchPage = {
  /**
   * Check if required elements exist on the page
   */
  checkDOMElements() {
    const resultsExist = document.querySelector(SEARCH_SELECTORS.resultsContainer);
    const filtersExist = document.querySelector(SEARCH_SELECTORS.filterForm);

    return {
      resultsExist,
      filtersExist,
    };
  },

  /**
   * Initialize search filters/facets
   * Hook into existing filter form logic if present
   */
  initFilters() {
    const filterForm = document.querySelector(SEARCH_SELECTORS.filterForm);

    if (!filterForm) {
      this.log('No filter form found, skipping filters init');
      return;
    }

    this.log('Initializing search filters');

    // TODO: Add filter-specific initialization
    // Examples:
    // - Bind filter change events
    // - Handle AJAX filter submission
    // - Toggle filter UI state
    // - Connect to analytics
  },

  /**
   * Initialize search results sorting
   */
  initSort() {
    const sortSelect = document.querySelector(SEARCH_SELECTORS.sortSelect);

    if (!sortSelect) {
      this.log('No sort select found, skipping sort init');
      return;
    }

    this.log('Initializing search sort');

    // TODO: Add sort-specific initialization
    // Examples:
    // - Listen for sort changes
    // - Update results via AJAX
    // - Track sort preference
  },

  /**
   * Initialize pagination or infinite scroll hooks
   */
  initPagination() {
    const pagination = document.querySelector(SEARCH_SELECTORS.paginationContainer);
    const infiniteScroll = document.querySelector(SEARCH_SELECTORS.infiniteScrollTrigger);

    if (!pagination && !infiniteScroll) {
      this.log('No pagination or infinite scroll found, skipping');
      return;
    }

    if (pagination) {
      this.log('Pagination detected');
      // TODO: Add pagination-specific logic
      // Examples:
      // - Smooth scroll to results on page change
      // - Analytics tracking
      // - Prefetch next page
    }

    if (infiniteScroll) {
      this.log('Infinite scroll detected');
      // TODO: Add infinite scroll-specific logic
      // Examples:
      // - Set up Intersection Observer
      // - Load next page on scroll
      // - Show loading state
      // - Handle end of results
    }
  },

  /**
   * Main initialization function
   */
  init() {
    this.log('Search page init starting');

    const { resultsExist } = this.checkDOMElements();

    // Exit early if we're not on search page
    if (!resultsExist) {
      this.log('Not a search results page, exiting');
      return;
    }

    // Initialize search features
    this.initFilters();
    this.initSort();
    this.initPagination();

    this.log('Search page init complete');

    // Custom event for other modules to hook into
    document.dispatchEvent(new CustomEvent('search:ready', {
      detail: {
        hasFilters: !!document.querySelector(SEARCH_SELECTORS.filterForm),
        hasSort: !!document.querySelector(SEARCH_SELECTORS.sortSelect),
      },
    }));
  },

  /**
   * Dev-only logging
   */
  log(message) {
    if (window.devLog) {
      window.devLog('SearchPage', message);
    }
  },
};

// ============================================================================
// BOOTSTRAP
// ============================================================================
// Use initOnce from theme.js to ensure this runs only once
function initSearchPage() {
  const searchResults = document.querySelector(SEARCH_SELECTORS.resultsContainer);
  if (searchResults && window.initOnce) {
    if (window.initOnce(searchResults, 'search-page')) {
      SearchPage.init();
    }
  } else if (searchResults) {
    SearchPage.init();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSearchPage);
} else {
  initSearchPage();
}
